"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Container from "@/components/core/container";
import Section from "@/components/core/section";
import { Project } from "@/models/project";
import Text from "@/components/core/text";
import Image from "next/image";
import { Heading, ScrollingHeading } from "@/components/core/heading";
import { getAssetUrl } from "@/lib/cms";
import { useLenis } from "lenis/react";
import { useRouter } from "next/navigation";
import Link from "next/link";

interface Props {
    projects: Project[];
    project: Project;
}

const phrases = [
    "Here we go",
    "Fasten your seatbelts",
    "Brace yourselves",
    "Drum roll please",
    "Ready or not",
];

export default function ProjectNext({
    project: currentProject,
    projects,
    ...props
}: Props) {
    const lenis = useLenis();
    const router = useRouter();

    const container = useRef<HTMLDivElement>(null);

    const [scrollY, setScrollY] = useState(0);
    const [maxScrollY, setMaxScrollY] = useState(0);
    const [change, setChange] = useState(false);
    const [phrase] = useState(
        phrases[Math.floor(Math.random() * phrases.length)],
    );

    const getNextItem = () => {
        const currentIndex = projects.findIndex(
            (project) => project.id === currentProject.id,
        );
        const nextIndex =
            currentIndex === -1 || currentIndex + 1 === projects.length
                ? 0
                : currentIndex + 1;
        return projects[nextIndex];
    };

    const nextProject = getNextItem();

    const go = useCallback(() => {
        if (change) return;

        setChange(true);

        if(lenis) {
          lenis.stop();
        }

        router.push(nextProject.url);
    }, [lenis, router, change, nextProject]);

    useEffect(() => {
        if (!lenis) return;

        lenis.start();
    }, [lenis]);

    useEffect(() => {
        if (!nextProject) return;

        const footer = document.querySelector("footer") as HTMLDivElement;
        const footerPush = document.querySelector(
            "#footer-push",
        ) as HTMLDivElement;

        if (footer) {
            footer.style.display = "none";
        }

        if (footerPush) {
            footerPush.style.display = "none";
        }

        const onResize = () => {
            setMaxScrollY(window.innerHeight / 2);
        };

        const onWheel = (e: WheelEvent) => {
            if (
                window.innerHeight + Math.round(window.scrollY) >=
                document.body.offsetHeight
            ) {
                let shouldNavigateToNextProject = false;

                setScrollY((previous) => {
                    let y = Math.max(
                        0,
                        Math.min(
                            window.innerHeight / 2,
                            (previous += e.deltaY),
                        ),
                    );

                    if (y > 0) {
                        if (lenis) {
                            lenis.stop();
                        }

                        if (y >= window.innerHeight / 2 - 10) {
                            shouldNavigateToNextProject = true;
                        }
                    } else {
                        if (lenis) {
                            lenis.start();
                        }
                    }

                    return y;
                });

                if (shouldNavigateToNextProject) {
                    go();
                }
            }
        };

        window.addEventListener("resize", onResize);
        window.addEventListener("wheel", onWheel, false);

        onResize();

        return () => {
            if (footer) {
                footer.style.display = "block";
            }

            if (footerPush) {
                footerPush.style.display = "block";
            }

            window.removeEventListener("resize", onResize);
            window.removeEventListener("wheel", onWheel, false);
        };
    }, [lenis, router, nextProject, go]);

    const next = () => {
        setScrollY(window.innerHeight / 2);

        setTimeout(() => {
            go();
        }, 300);
    };

    if (!nextProject) {
        return <></>;
    }

    return (
        <>
            <Link href={nextProject.url} className="sr-only focus:not-sr-only">
                Go to next case study
            </Link>
            <Section
                ref={container}
                className="not-sr-only relative h-screen overflow-hidden"
            >
                <Container width="fluid">
                    <div className="container pt-[15vh] lg:pt-[25vh] flex justify-between items-center">
                        <div className="max-w-md">
                            <Text className="hidden lg:block">
                                Keep scrolling for next case study
                            </Text>
                            <Text className="lg:hidden">
                                Have a look at our next case study
                            </Text>
                        </div>
                        <div className="relative z-40 invisible md:visible w-full max-w-96 rounded-sm overflow-hidden bg-slate-50/20">
                            <div
                                className="h-[4px] bg-primary transition-all duration-300"
                                style={{
                                    width: `${Math.floor((scrollY / maxScrollY) * 100)}%`,
                                }}
                            ></div>
                        </div>
                    </div>
                    <ScrollingHeading
                        speed="slow"
                        color="primary"
                        transform="uppercase"
                        size="18xl"
                    >
                        {nextProject.client.title} X 328 Studios
                    </ScrollingHeading>
                </Container>
                <div
                    className="absolute top-0 left-0 w-full z-10 min-h-full overflow-hidden pointer-events-none transition-transform duration-300"
                    style={{
                        transform: `translate(0, ${maxScrollY - scrollY}px)`,
                    }}
                >
                    {change && (
                        <div className="absolute top-0 left-0 w-full h-full z-[2] bg-primary/60">
                            <div className="container w-full h-full flex justify-center items-center">
                                <Heading
                                    color="white"
                                    size="title"
                                    align="center"
                                >
                                    {phrase}
                                </Heading>
                            </div>
                        </div>
                    )}
                    <div
                        className="absolute inset-0 z-[1] transition-transform duration-300"
                        style={{
                            transform: `translate(0, ${(maxScrollY - scrollY) * -0.9}px)`,
                        }}
                    >
                        <Image
                            priority={true}
                            src={getAssetUrl(nextProject.hero_image)}
                            width={nextProject.hero_image?.width}
                            height={nextProject.hero_image?.height}
                            alt={nextProject.hero_image.alt}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                maxWidth: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </div>
                </div>
                <button
                    onClick={next}
                    className="absolute top-0 left-0 w-full h-full z-20 lg:hidden flex justify-center items-end p-8 bg-gradient-to-t from-primary to-30%"
                >
                    {!change && (
                        <span className="font-bold text-4xl text-white uppercase">
                            Next
                        </span>
                    )}
                </button>
            </Section>
        </>
    );
}
